import type { MiaChatMessage, MiaErrorEvent, MiaPublicConfig, MiaSession, MiaWidgetState } from '../types';
import { createId } from '../utils';

export function createInitialState(conversationId?: string): MiaWidgetState {
  return {
    initialized: false,
    open: false,
    loading: false,
    sending: false,
    session: null,
    conversationId: conversationId || createId(),
    externalSubjectId: null,
    messages: [],
    config: null,
    error: null,
    destroyed: false,
  };
}

export class WidgetStore {
  private state: MiaWidgetState;
  private subscribers = new Set<(s: MiaWidgetState) => void>();

  constructor(initial?: Partial<MiaWidgetState>) {
    this.state = { ...createInitialState(), ...initial };
  }

  getState(): MiaWidgetState {
    return this.state;
  }

  subscribe(fn: (s: MiaWidgetState) => void): () => void {
    this.subscribers.add(fn);
    return () => this.subscribers.delete(fn);
  }

  private set(patch: Partial<MiaWidgetState>): void {
    this.state = { ...this.state, ...patch };
    for (const fn of this.subscribers) fn(this.state);
  }

  setBootstrap(config: MiaPublicConfig, session: MiaSession): void {
    this.set({ config, session, error: null, loading: false });
  }

  markInitialized(config: MiaPublicConfig, session: MiaSession): void {
    this.set({ initialized: true, config, session, error: null, loading: false });
  }

  setLoading(loading: boolean): void {
    this.set({ loading });
  }

  setSending(sending: boolean): void {
    this.set({ sending });
  }

  setOpen(open: boolean): void {
    this.set({ open });
  }

  setSession(session: MiaSession): void {
    this.set({ session });
  }

  setExternalSubjectId(id: string | null): void {
    this.set({ externalSubjectId: id });
  }

  setError(error: MiaErrorEvent | null): void {
    this.set({ error });
  }

  addMessage(role: MiaChatMessage['role'], content: string): MiaChatMessage {
    const msg: MiaChatMessage = {
      id: createId(),
      role,
      content,
      createdAt: Date.now(),
    };
    this.set({ messages: [...this.state.messages, msg] });
    return msg;
  }

  resetConversation(): void {
    this.set({ conversationId: createId(), messages: [] });
  }

  markDestroyed(): void {
    this.set({ destroyed: true, open: false, session: null, initialized: false });
  }
}
